import { useEffect, useRef } from 'react';
import type { OrderStage, TrackedOrder } from '@xshop/shared';
import { money, type StoreFacts } from './storeFacts';
import { DATE_LOCALES, type OrderLookupWords } from './orderLookupWords';

// A found order, as Shopify's order status page shows one: its number and date, how far it has come in
// three steps, where its parcel is tracked, its lines and its totals. A cancelled order says so instead
// of showing steps it will never reach.

/** The steps an order that was not cancelled moves through, in order. */
const STEPS: readonly OrderStage[] = ['placed', 'shipped', 'delivered'];

/** What the card needs to draw an order: the order, the words, and the store that prices it. */
type CardProps = { order: TrackedOrder; words: OrderLookupWords; store: StoreFacts | null; onAnother?: () => void };

// Spells an amount in the order's currency the way the store spells money, or plainly without a store.
function orderMoney(amount: number, order: TrackedOrder, store: StoreFacts | null): string {
  return store ? money(amount, { ...store, currency: order.currency }) : `${amount.toFixed(2)} ${order.currency}`;
}

// Draws the three steps, every step up to the order's own marked done and its own marked current.
const OrderSteps: React.FC<{ stage: OrderStage; words: OrderLookupWords }> = ({ stage, words }) => {
  const reached = STEPS.indexOf(stage);
  return (
    <ol className="xe-track-steps">
      {STEPS.map((step, index) => (
        <li key={step} data-done={index <= reached ? '' : undefined} aria-current={index === reached ? 'step' : undefined}>
          <span className="xe-track-dot" aria-hidden="true" />{words.stages[step]}
        </li>
      ))}
    </ol>
  );
};

// Draws where the parcel is tracked: the carrier and number, and a link to follow it when there is one.
const TrackingLine: React.FC<{ tracking: NonNullable<TrackedOrder['tracking']>; words: OrderLookupWords }> = ({ tracking, words }) => (
  <p className="xe-track-tracking">
    <span>{words.tracking}: <strong dir="ltr">{[tracking.company, tracking.number].filter(Boolean).join(' · ')}</strong></span>
    {tracking.url && <a href={tracking.url} target="_blank" rel="noreferrer">{words.trackParcel}</a>}
  </p>
);

// Draws the order's lines, each with its picture, name, quantity and price.
const OrderLines: React.FC<CardProps> = ({ order, words, store }) => (
  <ul className="xe-track-lines">
    {order.items.map((item, index) => (
      <li key={`${index}-${item.title}`}>
        {item.imageUrl ? <img src={item.imageUrl} alt="" width={64} height={64} loading="lazy" /> : <span className="xe-track-thumb" aria-hidden="true" />}
        <span className="xe-track-line-name">
          <span dir="auto">{item.title}</span>
          {item.variantTitle && <small dir="auto">{item.variantTitle}</small>}
          <small>{words.quantity(item.quantity)}</small>
        </span>
        <span className="xe-track-line-price">{orderMoney(item.totalPrice, order, store)}</span>
      </li>
    ))}
  </ul>
);

// Draws the totals under the lines: subtotal, shipping, any discount, and what is owed.
const OrderTotals: React.FC<CardProps> = ({ order, words, store }) => (
  <dl className="xe-track-totals">
    <div><dt>{words.subtotal}</dt><dd>{orderMoney(order.subtotal, order, store)}</dd></div>
    <div><dt>{words.shipping}</dt><dd>{order.shipping === 0 ? words.free : orderMoney(order.shipping, order, store)}</dd></div>
    {order.discount > 0 && <div><dt>{words.discount}</dt><dd>−{orderMoney(order.discount, order, store)}</dd></div>}
    <div className="xe-track-total"><dt>{words.total}</dt><dd>{orderMoney(order.total, order, store)}</dd></div>
  </dl>
);

// Draws one found order, with the way back to the lookup when the shopper found it themselves.
export const OrderStatusCard: React.FC<CardProps> = (props) => {
  const { order, words, store, onAnother } = props;
  const heading = useRef<HTMLHeadingElement>(null);
  const found = onAnother !== undefined;
  // A shopper who just found their order is taken to it, so a screen reader reads it out.
  useEffect(() => { if (found) heading.current?.focus(); }, [found, order]);
  const placed = new Date(order.placedAt).toLocaleDateString(DATE_LOCALES[store?.language ?? 'en'], { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <section className="xe-track-card" aria-label={words.order(order.orderNumber)}>
      <header className="xe-track-head">
        <h2 ref={heading} tabIndex={-1}>{words.order(order.orderNumber)}</h2>
        <p>{words.placedOn(placed)} · <span dir="auto">{words.deliveringTo(order.city)}</span></p>
      </header>
      {order.stage === 'cancelled' ? <p className="xe-track-cancelled" role="status">{words.cancelled}</p> : <OrderSteps stage={order.stage} words={words} />}
      {order.tracking && order.stage !== 'cancelled' && <TrackingLine tracking={order.tracking} words={words} />}
      <OrderLines {...props} />
      <OrderTotals {...props} />
      {onAnother && <button type="button" className="xe-track-another" onClick={onAnother}>{words.another}</button>}
    </section>
  );
};
